import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Box
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../context/AppContext";
import EditableForm from "../EditableForm";

// ✅ Import API calls
import {
    getEmployeeById,
    createEmployee,
    updateEmployee
} from "../../api/users";

const EmployeeDialog = ({ open, onClose, onSuccess, id }) => {
    const { t } = useTranslation();
    const { setIsLoading } = useAppContext();
    const [error, setError] = useState(null)

    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        role: "employee",
        is_active: true,
        salary: "",
        employment_date: ""
    });
    const [loading, setLoading] = useState(!!id);

    useEffect(() => {
        if (id) {
            const fetchData = async () => {
                setLoading(true);
                try {
                    const employee = await getEmployeeById(id);
                    setError(null);
                    setForm({
                        ...employee,
                        employment_date: employee.employment_date?.split("T")[0] || ""
                    });
                } catch (err) {
                    console.error("Error loading employee", err);
                    setError(err.message);
                } finally {
                    setLoading(false);
                }
            };
            fetchData();
        } else {
            setForm({
                name: "",
                email: "",
                phone: "",
                role: "employee",
                is_active: true,
                salary: "",
                employment_date: ""
            });
        }
    }, [id]);

    const handleSubmit = async (data) => {
        setIsLoading(true);
        try {
            if (id) {
                await updateEmployee(id, data);
            } else {
                await createEmployee(data);
            }
            setError(null);
            onSuccess?.();
            setForm({
                name: "",
                email: "",
                phone: "",
                role: "employee",
                is_active: true,
                salary: "",
                employment_date: ""
            });
            onClose();
        } catch (err) {
            console.error("Save error", err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const fields = [
        { name: "name", label: t("companyEmployees.name"), required: true },
        { name: "email", label: t("companyEmployees.email"), required: true },
        { name: "phone", label: t("companyEmployees.phone"), required: true }, {
            name: "role",
            label: t("companyEmployees.role"),
            required: true,
            type: "select",
            options: [
                { value: "employee", label: t("roles.employee") },
                { value: "manager", label: t("roles.manager") },
                { value: "admin", label: t("roles.admin") },
            ]
        },
        { name: "salary", label: t("companyEmployees.salary"), type: "number" },
        { name: "employment_date", label: t("companyEmployees.employment_date"), type: "date" },
    ];

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ color: "#000" }}>{id ? t("companyEmployees.edit") : t("companyEmployees.create")}</DialogTitle>
            <DialogContent sx={{ paddingTop: "5px !important" }}>
                {error && (
                    <Box mb={2}>
                        <Box
                            sx={{
                                bgcolor: "#fdecea",
                                color: "#b71c1c",
                                px: 2,
                                py: 1.5,
                                borderRadius: 1,
                                fontSize: 14,
                                fontWeight: 500,
                            }}
                        >
                            {t(`${error}`) || error}
                        </Box>
                    </Box>
                )}
                {loading ? (
                    <Box display="flex" justifyContent="center" my={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <EditableForm
                        data={form}
                        fields={fields}
                        onChange={setForm}
                        onSubmit={handleSubmit}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    sx={{
                        color: "#000"
                    }}>
                    {t("actions.cancel")}
                </Button>
            </DialogActions>
        </Dialog >
    );
};

export default EmployeeDialog;
